 // src/pages/BookingPage.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Form,
  Button,
  Calendar,
  DatePicker,
  Tag,
  Modal,
  Popover,
  Typography,
  Divider,
  Row,
  Col,
  Space,
  Affix,
  Alert,
  List,
  Tooltip,
  Table,
  Empty,
  Spin,
  Skeleton,
  Switch,
} from "antd";
import {
  CalendarOutlined,
  ReloadOutlined,
  EyeOutlined,
  PlusOutlined,
  UserAddOutlined,
  CheckOutlined,
  FileTextOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import minMax from "dayjs/plugin/minMax";
import GuestDrawer from "../components/GuestDrawer";
import "./BookingPage.css";
import { StatusTag, useFlatPage } from "../components/ui";
import notify from "../utils/notify";

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(minMax);

const { Text } = Typography;
const { RangePicker } = DatePicker;

/** ===== DEBUG SWITCH & HELPERS ===== */
const DEBUG = true;
const dlog = (...args) => { if (DEBUG) console.log(...args); };
const dgroup = (label) => DEBUG && console.groupCollapsed(label);
const dgroupEnd = () => DEBUG && console.groupEnd();
const dtable = (data, label) => { if (!DEBUG) return; if (label) console.log(label); console.table(data); };

/** Flatten segments to parts */
const flattenPieces = (arr) =>
  (arr || []).flatMap((s) =>
    s?.parts && Array.isArray(s.parts) && s.parts.length ? s.parts : [s]
  );

// ---------- Safe numeric helpers ----------
const num = (v) => Number(v ?? 0);
const getVatPct = (obj) => num(obj?.vatPercentage ?? obj?.VatPercentage);
const getLevies = (obj) => num(obj?.additionalLevies ?? obj?.AdditionalLevies);
const getDiscount = (obj) => num(obj?.discount ?? obj?.Discount);
const getPrice = (obj) => num(obj?.price ?? obj?.Price);

// ---------- Room type getters (robust to casing/shapes) ----------
const getTypeName = (r) =>
  r?.roomType?.typeName ??
  r?.RoomType?.TypeName ??
  r?.typeName ??
  r?.TypeName ??
  r?.roomTypeName ??
  r?.RoomTypeName ??
  null;

const getRoomTypeId = (r) =>
  r?.roomTypeId ??
  r?.RoomTypeId ??
  r?.roomType?.id ??
  r?.RoomType?.Id ??
  null;

// ---------- Per-night math ----------
const perNightFromRoom = (r) => {
  const price = getPrice(r);
  const disc = getDiscount(r);
  const base = price * (1 - disc / 100);
  const vat = base * (getVatPct(r) / 100);
  const lev = getLevies(r);
  return { base, vat, lev, total: base + vat + lev };
};

const nightsBetween = (start, end) => {
  const a = dayjs(start).startOf("day");
  const b = dayjs(end).startOf("day");
  return Math.max(b.diff(a, "day"), 0); // excludes checkout day
};

const BookingPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // toolbar
  const [dates, setDates] = useState([null, null]);
  const numDays = useMemo(() => {
    const [start, end] = dates || [];
    return start && end ? nightsBetween(start, end) : 0;
  }, [dates]);

  // data & selections
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomAvailability, setRoomAvailability] = useState([]); // of selected room
  const [selectedRange, setSelectedRange] = useState([]); // [startISO, endISO]
  const [segments, setSegments] = useState([]);

  // ui state
  const [guestDrawerOpen, setGuestDrawerOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  // loading flags
  const [loading, setLoading] = useState({
    rooms: false,
    availability: false,
    submitting: false,
  });
  const isBusy = !!(loading.rooms || loading.availability);

  // auto-supplement toggle
  const [allowSupplement, setAllowSupplement] = useState(false);

  // abort ctrl
  const roomsAbortRef = useRef();

  // number format
  const fmt = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const addedPieces = useMemo(() => flattenPieces(segments), [segments]);

    const addedPiecesForSelectedRoom = useMemo(() => {
      if (!selectedRoom?.roomId) return [];
      return addedPieces.filter(
        (p) => String(p.roomId) === String(selectedRoom.roomId)
      );
    }, [addedPieces, selectedRoom]);

    const isDateInAddedPieces = useCallback(
      (d) => {
        if (!addedPiecesForSelectedRoom.length) return null;

        const hit = addedPiecesForSelectedRoom.find((p) =>
          dayjs(d).isBetween(dayjs(p.startISO), dayjs(p.endISO), null, "[)")
        );

        return hit || null;
      },
      [addedPiecesForSelectedRoom]
    );


    const nightKeysBetween = (startISO, endISO) => {
  const out = [];
  let d = dayjs(startISO).startOf("day");
  const e = dayjs(endISO).startOf("day"); // checkout-exclusive
  while (d.isBefore(e, "day")) {
    out.push(d.format("YYYY-MM-DD"));
    d = d.add(1, "day");
  }
  return out;
};

const buildSegmentFromMeta = (meta, startISO, endISO) => {
  const nights = nightsBetween(startISO, endISO);

  const price = Number(meta.pricePerNight ?? meta.price ?? 0);
  const discount = Number(meta.discount ?? 0);
  const vatPct = Number(meta.vatPercentage ?? 0);
  const levies = Number(meta.additionalLevies ?? 0);

  const baseAfter = price * (1 - discount / 100);
  const vat = baseAfter * (vatPct / 100);
  const nightly = baseAfter + vat + levies;
  const totalPrice = nightly * nights;

  return {
    ...meta,
    startISO,
    endISO,
    nights,
    segmentPrice: totalPrice,
  };
};


// Map nightKey -> { roomId, roomNumber }
const buildNightCoverage = (pieces) => {
  const map = new Map();
  const overlaps = []; // nights assigned more than once

  for (const p of pieces) {
    const nights = nightKeysBetween(p.startISO, p.endISO);
    for (const nk of nights) {
      if (map.has(nk)) {
        overlaps.push({
          night: nk,
          existing: map.get(nk),
          incoming: { roomId: p.roomId, roomNumber: p.roomNumber },
        });
      } else {
        map.set(nk, { roomId: p.roomId, roomNumber: p.roomNumber });
      }
    }
  }
  return { map, overlaps };
};


  // ==== LOG current split whenever segments change ====
  useEffect(() => {
    if (!DEBUG) return;
    const parts = flattenPieces(segments);
    const rows = parts.map((p, i) => ({
      "#": i + 1,
      Room: p.roomNumber,
      RoomId: p.roomId,
      From: p.startISO,
      To: p.endISO,
      Nights: nightsBetween(p.startISO, p.endISO),
      PricePerNight: p.pricePerNight,
      DiscountPct: p.discount,
      VATpct: p.vatPercentage,
      Levies: p.additionalLevies,
      SegmentPrice: p.segmentPrice,
    }));
    dgroup("📒 SEGMENTS / SPLIT (flattened)");
    dtable(rows);
    dgroupEnd();
  }, [segments]);

  // Distinct segment room numbers (for the chip after "Price detail")
  const segmentRoomNumbers = useMemo(() => {
    const nums = new Set();

    for (const s of segments) {
      const parts = Array.isArray(s?.parts) && s.parts.length ? s.parts : [s];
      for (const p of parts) {
        if (p?.roomNumber != null) {
          nums.add(String(p.roomNumber));
        } else if (p?.roomId != null) {
          const rr = (availableRooms || []).find(
            (r) => String(r.roomId) === String(p.roomId)
          );
          if (rr?.roomNumber != null) nums.add(String(rr.roomNumber));
        }
      }
    }

    if (nums.size === 0 && selectedRoom && selectedRange.length === 2) {
      if (selectedRoom.roomNumber != null) nums.add(String(selectedRoom.roomNumber));
    }

    return Array.from(nums).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    );
  }, [segments, selectedRoom, selectedRange, availableRooms]);

  const roomsTagNode = useMemo(() => {
    if (!segmentRoomNumbers.length) return null;
    const maxInline = 4;
    const inline = segmentRoomNumbers.slice(0, maxInline);
    const remaining = segmentRoomNumbers.length - inline.length;
    const label = `Rooms: ${inline.join(", ")}${remaining > 0 ? ` +${remaining} more` : ""}`;
    return (
      <Popover
        title="Segments rooms"
        content={
          <div style={{ maxWidth: 280, whiteSpace: "normal" }}>
            {segmentRoomNumbers.join(", ")}
          </div>
        }
      >
        <Tag className="bk-guest" style={{ margin: 0 }}>{label}</Tag>
      </Popover>
    );
  }, [segmentRoomNumbers]);

  const calendarKey = useMemo(() => {
    const a = dates?.[0] ? dayjs(dates[0]).format("YYYY-MM-DD") : "null";
    const b = dates?.[1] ? dayjs(dates[1]).format("YYYY-MM-DD") : "null";
    return `${a}|${b}|${selectedRoom?.roomId ?? "none"}`;
  }, [dates, selectedRoom]);

  const dayInSeg = (d, seg) => {
    const a = dayjs(seg.start).startOf("day");
    const b = dayjs(seg.end).startOf("day"); // exclusive
    return d.isSame(a, "day") || (d.isAfter(a, "day") && d.isBefore(b, "day"));
  };

  // ---------- Normalizer ----------
  const normalizeRoom = (r, rangeStart, rangeEnd) => {
    const rawSegs = r?.availabilitySegments ?? r?.AvailabilitySegments ?? [];
    const segs = (Array.isArray(rawSegs) ? rawSegs : []).map((s) => ({
      start: s.start ?? s.Start,
      end: s.end ?? s.End,
      status: s.status ?? s.Status,
    }));

    if (!rangeStart || !rangeEnd) {
      return { ...r, availabilitySegments: segs, status: "Unknown", isAvailable: false, conflictingDates: [] };
    }

    const reqStart = dayjs(rangeStart).startOf("day");
    const reqEnd = dayjs(rangeEnd).startOf("day");
    if (!reqEnd.isAfter(reqStart, "day")) {
      return { ...r, availabilitySegments: segs, status: "Unknown", isAvailable: false, conflictingDates: [] };
    }

    let hasAvail = false;
    let hasBooked = false;
    const conflicts = [];

    let d = reqStart.clone();
    while (d.isBefore(reqEnd, "day")) {
      const seg = segs.find((s) => dayInSeg(d, s));
      const isAvail = !!seg && seg.status === "Available";
      if (isAvail) hasAvail = true;
      else {
        hasBooked = true;
        conflicts.push(d.format("YYYY-MM-DD"));
      }
      d = d.add(1, "day");
    }

    const status =
      hasAvail && !hasBooked
        ? "Available"
        : hasAvail && hasBooked
        ? "PartiallyAvailable"
        : "Booked";

    return {
      ...r,
      availabilitySegments: segs,
      status,
      isAvailable: status === "Available",
      conflictingDates: conflicts,
    };
  };

  // currency guard (for split stays)
  const [currencyMismatch, setCurrencyMismatch] = useState(false);
  useEffect(() => {
    if (segments.length <= 1) {
      setCurrencyMismatch(false);
      return;
    }
    const segCurrency = (s) => s.currency || s?.parts?.[0]?.currency || "BWP";
    const first = segCurrency(segments[0]);
    setCurrencyMismatch(segments.some((s) => segCurrency(s) !== first));
  }, [segments]);

  // Derived totals for segments (supports composite parts)
  const segmentsBreakdown = useMemo(() => {
    if (!segments.length) return null;

    let base = 0, vat = 0, levies = 0, discountTotal = 0, total = 0, nightsTotal = 0;

    const eachPiece = (s) =>
      s?.parts && Array.isArray(s.parts) && s.parts.length ? s.parts : [s];

    for (const s of segments) {
      for (const pce of eachPiece(s)) {
        const nights = pce.nights || nightsBetween(pce.startISO, pce.endISO) || 0;
        const baseAfter = Number(pce.pricePerNight || 0) * (1 - Number(pce.discount || 0) / 100);
        const vatAmt = baseAfter * (Number(pce.vatPercentage || 0) / 100);
        const lev = Number(pce.additionalLevies || 0);
        const nightly = baseAfter + vatAmt + lev;
        const segTotal = Number.isFinite(pce.segmentPrice) ? Number(pce.segmentPrice) : nightly * nights;

        const preBase = Number(pce.pricePerNight || 0) * nights;
        const basePart = baseAfter * nights;
        const vatPart = vatAmt * nights;
        const levPart = lev * nights;
        const segDiscount = Math.max(preBase - basePart, 0);

        base += basePart;
        vat += vatPart;
        levies += levPart;
        total += segTotal;
        discountTotal += segDiscount;
        nightsTotal += nights;
      }
    }

    return { base, vat, levies, discountTotal, total, nightsTotal };
  }, [segments]);

  // Sync money fields
  useEffect(() => {
    if (segments.length > 0 && segmentsBreakdown) {
      const avgDiscountPerNight =
        segmentsBreakdown.nightsTotal > 0
          ? segmentsBreakdown.discountTotal / segmentsBreakdown.nightsTotal
          : 0;

      const firstCurrency =
        segments[0]?.currency ||
        segments[0]?.parts?.[0]?.currency ||
        selectedRoom?.currency ||
        "BWP";

      form.setFieldsValue({
        basePrice: segmentsBreakdown.base,
        vatAmount: segmentsBreakdown.vat,
        leviesAmount: segmentsBreakdown.levies,
        discount: segmentsBreakdown.discountTotal,
        discountPerDay: avgDiscountPerNight,
        totalAmount: segmentsBreakdown.total,
        currency: firstCurrency,
      });
      return;
    }

    if (!selectedRoom || !numDays) {
      form.setFieldsValue({
        basePrice: 0,
        discountPerDay: 0,
        discount: 0,
        totalAmount: 0,
        currency: selectedRoom?.currency || "BWP",
        vatAmount: 0,
        leviesAmount: 0,
      });
      return;
    }

    const p = perNightFromRoom(selectedRoom);
    const baseTotal = p.base * numDays;
    const vatTotal = p.vat * numDays;
    const levTotal = p.lev * numDays;
    const amount = p.total * numDays;

    const baseBefore = Number(selectedRoom.price || 0) * numDays;
    const impliedDiscount = baseBefore - baseTotal;

    form.setFieldsValue({
      basePrice: baseTotal,
      discountPerDay: numDays ? impliedDiscount / numDays : 0,
      discount: impliedDiscount,
      totalAmount: amount,
      currency: selectedRoom.currency || "BWP",
      vatAmount: vatTotal,
      leviesAmount: levTotal,
    });
  }, [segments, segmentsBreakdown, selectedRoom, numDays, form]);

  // ---------- Fetch available rooms ----------
  const fetchAvailableRooms = async (inDate, outDate, opts = {}) => {
    const { preserveSelection = false } = opts;
    if (!authData?.hotelId || !inDate || !outDate) return [];
    roomsAbortRef.current?.abort();
    const controller = new AbortController();
    roomsAbortRef.current = controller;

    try {
      setLoading((s) => ({ ...s, rooms: true }));
      const { data } = await axiosInstance.get("/Bookings/AvailableRooms", {
        params: {
          hotelId: authData.hotelId,
          checkInDate: inDate.toISOString(),
          checkOutDate: outDate.toISOString(),
          includeAvailability: true,
        },
        signal: controller.signal,
      });

      const baseRooms = (Array.isArray(data) ? data : []).map((r) =>
        normalizeRoom(r, inDate, outDate)
      );

      const enriched = await Promise.all(
        baseRooms.map(async (room) => {
          let next = { ...room };

          // price enrich
          try {
            const { data: p } = await axiosInstance.get("/Price/roomprice", {
              params: { hotelId: authData.hotelId, roomId: room.roomId },
              signal: controller.signal,
            });
            next = {
              ...next,
              price: Number(p?.price ?? next.price ?? 0),
              discount: Number(p?.discount ?? next.discount ?? 0),
              vatPercentage: Number(p?.vatPercentage ?? next.vatPercentage ?? 0),
              additionalLevies: Number(
                p?.additionalLevies ?? next.additionalLevies ?? 0
              ),
              currency: p?.currency || next.currency || "BWP",
            };
          } catch {
            // ignore price fetch failure
          }

          // ensure normalized type fields from current payload
          next.typeName = getTypeName(next) || next.typeName || null;
          next.roomTypeId = getRoomTypeId(next) || next.roomTypeId || null;

          // fallback: if still missing, load the room once
          if (!next.typeName || !next.roomTypeId) {
            try {
              const { data: rd } = await axiosInstance.get(
                `/Rooms/${room.roomId}`,
                { signal: controller.signal }
              );
              next = {
                ...next,
                typeName: getTypeName(rd) || next.typeName || null,
                roomTypeId: getRoomTypeId(rd) || next.roomTypeId || null,
                roomType: rd?.roomType ?? rd?.RoomType ?? next.roomType,
              };
            } catch {
              // ignore
            }
          }

          return next;
        })
      );

      const finalRooms = enriched.map((r) => normalizeRoom(r, inDate, outDate));
      dgroup("🏨 fetchAvailableRooms()");
      dlog(
        "Date window:",
        inDate.format("YYYY-MM-DD"),
        "→",
        outDate.format("YYYY-MM-DD")
      );
      dtable(
        finalRooms.map((r) => ({
          Room: r.roomNumber,
          RoomId: r.roomId,
          Status: r.status,
          Conflicts: (r.conflictingDates || []).length,
          Price: r.price,
          Discount: r.discount,
          VATpct: r.vatPercentage,
          Levies: r.additionalLevies,
        }))
      );
      dgroupEnd();

      setAvailableRooms(finalRooms);

      if (preserveSelection && selectedRoom?.roomId) {
      const still = finalRooms.find(r => r.roomId === selectedRoom.roomId);
      if (!still) {
        setSelectedRoom(null);
        setRoomAvailability([]);
      } else {
        // ✅ refresh selected room availability using the same request window
        handleSelectRoom(still, true, { ci: inDate, co: outDate });
      }
    } else {
      if (finalRooms.length > 0) {
        // ✅ IMPORTANT: pass the window so calendar availability is correct on first load
        handleSelectRoom(finalRooms[0], true, { ci: inDate, co: outDate });
      } else {
        setSelectedRoom(null);
        setRoomAvailability([]);
      }
    }

      return finalRooms;
    } catch (e) {
      if (e.name !== "CanceledError") {
        console.error(e);
        notify.error(e.response?.data?.message || "Failed to fetch rooms");
      }
      return [];
    } finally {
      setLoading((s) => ({ ...s, rooms: false }));
    }
  };
 


  // helpers (add once)
const toDateParam = (d) => dayjs(d).format("YYYY-MM-DD");
const normStatus = (s) => String(s ?? "").trim().toLowerCase();

// ✅ accept window explicitly (ci/co) instead of relying on state timing
const fetchRoomAvailability = async (roomId, ci, co) => {
  if (!ci || !co) return [];

  try {
    setLoading((s) => ({ ...s, availability: true }));

    const { data } = await axiosInstance.get(
      `/Bookings/RoomAvailability/${roomId}`,
      {
        params: {
          hotelId: authData.hotelId,              // include if your API supports/needs it
          checkInDate: toDateParam(ci),           // ✅ date-only
          checkOutDate: toDateParam(co),          // ✅ date-only
        },
      }
    );

    const segs =
      data?.availabilitySegments ??
      data?.AvailabilitySegments ??
      data ??
      [];

    const normalized = (Array.isArray(segs) ? segs : []).map((s) => ({
      start: s.start ?? s.Start,
      end: s.end ?? s.End,
      status: s.status ?? s.Status,
    }));

    // debug (optional but recommended)
    if (DEBUG) {
      console.groupCollapsed("📆 fetchRoomAvailability");
      //console.log("roomId:", roomId, "window:", toDateParam(ci), "→", toDateParam(co));
      //console.log("raw:", data);
      console.table(normalized);
      console.groupEnd();
    }

    setRoomAvailability(normalized);
    return normalized;
  } catch (e) {
    console.error(e);
    notify.error("Failed to load room availability");
    setRoomAvailability([]);
    return [];
  } finally {
    setLoading((s) => ({ ...s, availability: false }));
  }
};


const deriveDefaultSelectedRange = (availabilitySegs, ci, co) => {
  if (!ci || !co) return [];

  const winStart = dayjs(ci).startOf("day");
  const winEnd = dayjs(co).startOf("day"); // checkout (exclusive for nights)

  // intersect available segments with the window
  const blocks = (availabilitySegs || [])
    .filter((s) => s.status === "Available")
    .map((s) => {
      const a = dayjs(s.start).startOf("day");
      const b = dayjs(s.end).startOf("day"); // exclusive
      const start = a.isAfter(winStart) ? a : winStart;
      const end = b.isBefore(winEnd) ? b : winEnd;
      const nights = end.diff(start, "day");
      return nights > 0 ? { start, end, nights } : null;
    })
    .filter(Boolean);

  if (!blocks.length) return [];

  // Prefer full coverage if available; else take the longest block
  const full = blocks.find(
    (x) => x.start.isSame(winStart, "day") && x.end.isSame(winEnd, "day")
  );

  const best = full || blocks.sort((a, b) => b.nights - a.nights)[0];

  return [best.start.format("YYYY-MM-DD"), best.end.format("YYYY-MM-DD")];
};


 // initial load
useEffect(() => {
  const checkIn = dayjs().add(1, "day").startOf("day");
  const checkOut = checkIn.add(2, "day");
  setDates([checkIn, checkOut]);
  fetchAvailableRooms(checkIn, checkOut, { preserveSelection: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// handleSelectRoom: pass current window explicitly
const handleSelectRoom = async (room, silent = false, rangeOverride) => {
  const ci = rangeOverride?.ci ?? dates?.[0];
  const co = rangeOverride?.co ?? dates?.[1];

  try {
    const { data: p } = await axiosInstance.get("/Price/roomprice", {
      params: { hotelId: authData.hotelId, roomId: room.roomId },
    });

    const detailed = {
      ...room,
      price: num(p?.price ?? room.price ?? 0),
      currency: p?.currency ?? room.currency ?? "BWP",
      discount: num(p?.discount ?? room.discount ?? 0),
      vatPercentage: num(p?.vatPercentage ?? room.vatPercentage ?? 0),
      additionalLevies: num(p?.additionalLevies ?? room.additionalLevies ?? 0),
      typeName: getTypeName(room) || room.typeName || null,
      roomTypeId: getRoomTypeId(room) || room.roomTypeId || null,
    };

    setSelectedRoom(detailed);

    await fetchRoomAvailability(room.roomId, ci, co);

    if (!silent) notify.success(`Selected Room ${room.roomNumber}`);
  } catch {
    setSelectedRoom({
      ...room,
      typeName: getTypeName(room) || room.typeName || null,
      roomTypeId: getRoomTypeId(room) || room.roomTypeId || null,
    });
    await fetchRoomAvailability(room.roomId, ci, co);
    if (!silent) notify.warning("Selected room; price detail pending.");
  }
};


  const onDatesChange = async (val) => {
    if (!val || val.length !== 2 || !val[0] || !val[1]) {
      setDates([null, null]);
      setAvailableRooms([]);
      setSelectedRoom(null);
      setRoomAvailability([]);
      setSegments([]);
      setSelectedRange([]);
      form.setFieldsValue({
        basePrice: 0,
        discountPerDay: 0,
        discount: 0,
        totalAmount: 0,
        vatAmount: 0,
        leviesAmount: 0,
      });
      return;
    }

    const [ci, co] = val;
    if (co.diff(ci, "day") <= 0) {
      notify.warning("Check-out must be after check-in");
      return;
    }

    dgroup("🗓️ onDatesChange()");
    dlog("Global window:", ci.format("YYYY-MM-DD"), "→", co.format("YYYY-MM-DD"));
    dgroupEnd();

    setDates([ci, co]);
    setSelectedRange([]);
    setSegments([]);
    form.setFieldsValue({ dateRange: val });

    const rooms = await fetchAvailableRooms(ci, co, { preserveSelection: true });

    if (
      selectedRoom?.roomId &&
      rooms.some((r) => r.roomId === selectedRoom.roomId)
    ) {
      await fetchRoomAvailability(selectedRoom.roomId);
    } else {
      setRoomAvailability([]);
    }
  };

  const buildSegmentFromSelection = useCallback(() => {
    if (!selectedRoom || selectedRange.length < 2) return null;
    const [startISO, endISO] = selectedRange;
    const nights = nightsBetween(startISO, endISO);

    const baseAfter =
      Number(selectedRoom.price || 0) *
      (1 - Number(selectedRoom.discount || 0) / 100);
    const vat = baseAfter * (Number(selectedRoom.vatPercentage || 0) / 100);
    const nightly = baseAfter + vat + Number(selectedRoom.additionalLevies || 0);
    const totalPrice = nightly * nights;

    return {
      roomId: selectedRoom.roomId,
      roomNumber: selectedRoom.roomNumber,
      roomTypeId: getRoomTypeId(selectedRoom),
      roomTypeName: getTypeName(selectedRoom) || "",
      currency: selectedRoom.currency ?? "BWP",
      pricePerNight: Number(selectedRoom.price) || 0,
      discount: Number(selectedRoom.discount || 0),
      vatPercentage: Number(selectedRoom.vatPercentage || 0),
      additionalLevies: Number(selectedRoom.additionalLevies || 0),
      startISO,
      endISO,
      nights,
      segmentPrice: totalPrice,
    };
  }, [selectedRoom, selectedRange]);

  // ---------- Auto-supplement helpers ----------
  const condenseDatesToRanges = (conflictDays) => {
    if (!Array.isArray(conflictDays) || conflictDays.length === 0) return [];
    const days = [...new Set(conflictDays)].sort();
    const ranges = [];
    let runStart = dayjs(days[0]).startOf("day");
    let prev = runStart.clone();

    for (let i = 1; i < days.length; i++) {
      const d = dayjs(days[i]).startOf("day");
      if (d.diff(prev, "day") === 1) {
        prev = d;
      } else {
        ranges.push([runStart.clone(), prev.clone().add(1, "day")]); // [start, checkout)
        runStart = d;
        prev = d;
      }
    }
    ranges.push([runStart.clone(), prev.clone().add(1, "day")]);
    return ranges;
  };

  const canRoomCoverRange = (room, start, end) => {
    const segs = room?.availabilitySegments || [];
    let d = dayjs(start).startOf("day");
    const e = dayjs(end).startOf("day");
    while (d.isBefore(e, "day")) {
      const hit = segs.find((s) => dayInSeg(d, s) && s.status === "Available");
      if (!hit) return false;
      d = d.add(1, "day");
    }
    return true;
  };

  const buildSegmentForRoom = (room, start, end) => {
    const nights = nightsBetween(start, end);
    const price = Number(room.price || 0);
    const discount = Number(room.discount || 0);
    const vatPct = Number(room.vatPercentage || 0);
    const levies = Number(room.additionalLevies || 0);

    const baseAfter = price * (1 - discount / 100);
    theVat: {
    }
    const vat = baseAfter * (vatPct / 100);
    const nightly = baseAfter + vat + levies;
    const totalPrice = nightly * nights;

    return {
      roomId: room.roomId,
      roomNumber: room.roomNumber,
      roomTypeId: getRoomTypeId(room),
      roomTypeName: getTypeName(room) || "",
      currency: room.currency || "BWP",
      pricePerNight: price,
      discount,
      vatPercentage: vatPct,
      additionalLevies: levies,
      startISO: dayjs(start).format("YYYY-MM-DD"),
      endISO: dayjs(end).format("YYYY-MM-DD"),
      nights,
      segmentPrice: totalPrice,
    };
  };

  const autoFillConflicts = () => {
    if (
      !selectedRoom ||
      !Array.isArray(selectedRoom.conflictingDates) ||
      selectedRoom.conflictingDates.length === 0
    ) {
      notify.info("No conflicts to supplement.");
      return;
    }
    if (!dates?.[0] || !dates?.[1]) {
      notify.warning("Pick a global date range first.");
      return;
    }

    const sameTypeRooms = (availableRooms || []).filter(
      (r) =>
        getRoomTypeId(r) === getRoomTypeId(selectedRoom) &&
        r.roomId !== selectedRoom.roomId
    );

    if (sameTypeRooms.length === 0) {
      notify.warning(
        "No other rooms of the same type are loaded for these dates."
      );
      return;
    }

    const ranges = condenseDatesToRanges(selectedRoom.conflictingDates);
    const toAdd = [];
    const notCovered = [];

    dgroup("🧩 autoFillConflicts()");
    dlog("SelectedRoom:", {
      roomNumber: selectedRoom.roomNumber,
      roomId: selectedRoom.roomId,
      conflicts: selectedRoom.conflictingDates,
    });
    dtable(
      ranges.map(([s, e], i) => ({
        "#": i + 1,
        Start: s.format("YYYY-MM-DD"),
        End: e.format("YYYY-MM-DD"),
      })),
      "Conflict ranges"
    );

    for (const [rs, re] of ranges) {
      // 1) Try a single room that covers the whole range
      let covered = false;
      for (const room of sameTypeRooms) {
        if (canRoomCoverRange(room, rs, re)) {
          toAdd.push(buildSegmentForRoom(room, rs, re));
          covered = true;
          break;
        }
      }

      // 2) Greedy per-day coverage grouped by consecutive days on same room
      if (!covered) {
        let curRoom = null;
        let curStart = null;
        let d = rs.clone();
        while (d.isBefore(re, "day")) {
          const roomForDay = sameTypeRooms.find((room) =>
            (room.availabilitySegments || []).some(
              (s) => dayInSeg(d, s) && s.status === "Available"
            )
          );
          if (!roomForDay) {
            if (curRoom && curStart) {
              toAdd.push(buildSegmentForRoom(curRoom, curStart, d));
              curRoom = null;
              curStart = null;
            }
            notCovered.push(d.format("YYYY-MM-DD"));
            d = d.add(1, "day");
            continue;
          }

          if (!curRoom) {
            curRoom = roomForDay;
            curStart = d.clone();
          } else if (curRoom.roomId !== roomForDay.roomId) {
            toAdd.push(buildSegmentForRoom(curRoom, curStart, d));
            curRoom = roomForDay;
            curStart = d.clone();
          }
          d = d.add(1, "day");
        }
        if (curRoom && curStart) {
          toAdd.push(buildSegmentForRoom(curRoom, curStart, re));
        }
      }
    }

    // De-duplicate against existing segments (flattened)
    const existingKeySet = new Set(
      flattenPieces(segments).map((s) => `${s.roomId}|${s.startISO}|${s.endISO}`)
    );
    const uniqueAdds = toAdd.filter(
      (s) => !existingKeySet.has(`${s.roomId}|${s.startISO}|${s.endISO}`)
    );

    dtable(
      uniqueAdds.map((p, i) => ({
        "#": i + 1,
        Room: p.roomNumber,
        RoomId: p.roomId,
        From: p.startISO,
        To: p.endISO,
        Nights: p.nights,
        SegPrice: p.segmentPrice,
      })),
      "Segments to add"
    );
    if (notCovered.length) dlog("Uncovered dates:", notCovered);
    dgroupEnd();

    if (uniqueAdds.length === 0 && notCovered.length === 0) {
      notify.info("Nothing to add. Conflicts are already covered.");
      return;
    }

    setSegments((prev) =>
      [...prev, ...uniqueAdds].sort(
        (a, b) => dayjs(a.startISO).valueOf() - dayjs(b.startISO).valueOf()
      )
    );

    if (uniqueAdds.length > 0) {
      notify.success(
        `Added ${uniqueAdds.length} supplemental segment(s) to cover conflicts.`
      );
    }
    if (notCovered.length > 0) {
      notify.warning(
        `Some dates could not be covered: ${notCovered
          .map((d) => dayjs(d).format("MMM D"))
          .join(", ")}`
      );
    }
  };

// merge new pieces into `segments`, de-duping exact (roomId, start, end)
function addPiecesNoOverlap(pieces) {
  setSegments((prev) => {
    const prevPieces = flattenPieces(prev);

    // Current coverage per night across ALL rooms
    const { map: covered } = buildNightCoverage(prevPieces);

    const outAdds = [];

    for (const p of pieces) {
      // Expand into per-night, then rebuild contiguous uncovered ranges
      const nights = nightKeysBetween(p.startISO, p.endISO);
      let runStart = null;

     const flush = (endISO) => {
      if (!runStart) return;

      // runStart and endISO are both YYYY-MM-DD (endISO is checkout day)
      outAdds.push(buildSegmentFromMeta(p, runStart, endISO));
      runStart = null;
    };


      for (let i = 0; i < nights.length; i++) {
        const nk = nights[i];
        const nextCheckout = dayjs(nk).add(1, "day").format("YYYY-MM-DD");

        if (!covered.has(nk)) {
          if (!runStart) runStart = nk;
          // tentatively mark covered to prevent overlaps within the same add batch
          covered.set(nk, { roomId: p.roomId, roomNumber: p.roomNumber });
        } else {
          // this night already assigned — close current run
          flush(nk);
        }

        // if last night, close run at its checkout
        if (i === nights.length - 1) {
          flush(nextCheckout);
        }
      }
    }

    // De-dupe exact same (roomId,start,end)
    const keyOf = (x) => `${x.roomId}|${x.startISO}|${x.endISO}`;
    const existing = new Set(prevPieces.map(keyOf));
    const uniqueAdds = outAdds.filter((x) => !existing.has(keyOf(x)));

    if (!uniqueAdds.length) return prev;

    const merged = [...prev, ...uniqueAdds].sort(
      (a, b) =>
        dayjs(a.startISO || a.parts?.[0]?.startISO).valueOf() -
        dayjs(b.startISO || b.parts?.[0]?.startISO).valueOf()
    );

    return merged;
  });
}

/** Add this room for the full top date-range, regardless of conflicts. 
 * If allowPartial=true and there's no full coverage, it adds the available blocks inside the window. */
const addRoomCoverRemaining = (room) => {
  const [ci, co] = dates || [];
  if (!ci || !co) return;

  const envelopeStart = dayjs(ci).startOf("day");
  const envelopeEnd = dayjs(co).startOf("day");

  const existingPieces = flattenPieces(segments);
  const { map: covered } = buildNightCoverage(existingPieces);

  const toAdd = [];
  let runStart = null;

  let d = envelopeStart.clone();
  while (d.isBefore(envelopeEnd, "day")) {
    const nk = d.format("YYYY-MM-DD");
    const roomCan = canRoomCoverRange(room, d, d.add(1, "day"));
    const uncovered = !covered.has(nk);

    if (uncovered && roomCan) {
      if (!runStart) runStart = d.clone();
    } else {
      if (runStart) {
        toAdd.push(buildSegmentForRoom(room, runStart, d));
        runStart = null;
      }
    }

    d = d.add(1, "day");
  }

  if (runStart) {
    toAdd.push(buildSegmentForRoom(room, runStart, envelopeEnd));
  }

  if (!toAdd.length) {
    notify.info(`Room ${room.roomNumber} has no remaining nights to cover in this window.`);
    return;
  }

  addPiecesNoOverlap(toAdd);
  notify.success(`Added ${toAdd.length} segment(s) from room ${room.roomNumber} to cover remaining nights.`);
};




  // --------- Composite "Add Segment (Date Range)" ----------
  const addCompositeFromDateRange = () => {
    if (!selectedRoom) {
      notify.warning("Pick a room first.");
      return;
    }
    if (!dates?.[0] || !dates?.[1]) {
      notify.warning("Pick the Date range at the top first.");
      return;
    }

    const winStart = dayjs(dates[0]).startOf("day");
    const winEnd = dayjs(dates[1]).startOf("day");
    if (!winEnd.isAfter(winStart, "day")) {
      notify.warning("Check-out must be after check-in");
      return;
    }

    // Blocks the selected room CAN cover inside the window (end exclusive)
    const availBlocks = [];
    const segs = (roomAvailability || []).filter((s) => s.status === "Available");
    for (const s of segs) {
      const a = dayjs(s.start).startOf("day");
      const b = dayjs(s.end).startOf("day"); // end exclusive
      const start = a.isAfter(winStart) ? a : winStart;
      const end = b.isBefore(winEnd) ? b : winEnd;
      if (end.isAfter(start, "day")) {
        availBlocks.push({ start, end });
      }
    }

    const conflictDays = (selectedRoom.conflictingDates || [])
      .map((x) => dayjs(x).startOf("day"))
      .filter((d) => !d.isBefore(winStart, "day") && d.isBefore(winEnd, "day"))
      .map((d) => d.format("YYYY-MM-DD"));
    const conflictRanges = condenseDatesToRanges(conflictDays).map(([s, e]) => ({
      start: s,
      end: e,
    }));

    dgroup("🧱 addCompositeFromDateRange()");
    dlog("Window:", winStart.format("YYYY-MM-DD"), "→", winEnd.format("YYYY-MM-DD"));
    dtable(
      availBlocks.map((b, i) => ({
        "#": i + 1,
        From: b.start.format("YYYY-MM-DD"),
        To: b.end.format("YYYY-MM-DD"),
      })),
      "Selected room available blocks"
    );
    dtable(
      conflictRanges.map((b, i) => ({
        "#": i + 1,
        From: b.start.format("YYYY-MM-DD"),
        To: b.end.format("YYYY-MM-DD"),
      })),
      "Conflict ranges to cover"
    );

    const sameTypeRooms = availableRooms.filter(
      (r) =>
        r.roomId !== selectedRoom.roomId && getRoomTypeId(r) === getRoomTypeId(selectedRoom)
    );
    dtable(
      sameTypeRooms.map((r) => ({ Room: r.roomNumber, RoomId: r.roomId })),
      "Same-type candidate rooms"
    );

    const parts = [];

    // parts from the selected room
    for (const b of availBlocks) {
      parts.push(buildSegmentForRoom(selectedRoom, b.start, b.end));
    }

    // parts to cover conflicts
    for (const b of conflictRanges) {
      const pool = sameTypeRooms;
      // try single room full cover
      let picked = pool.find((r) => canRoomCoverRange(r, b.start, b.end)) || null;
      if (picked) {
        parts.push(buildSegmentForRoom(picked, b.start, b.end));
        continue;
      }
      // greedy per-day grouping if needed
      let curRoom = null;
      let curStart = null;
      let d = b.start.clone();
      while (d.isBefore(b.end, "day")) {
        const roomForDay = pool.find((room) =>
          (room.availabilitySegments || []).some(
            (s) => dayInSeg(d, s) && s.status === "Available"
          )
        );
        if (!roomForDay) {
          if (curRoom && curStart) {
            parts.push(buildSegmentForRoom(curRoom, curStart, d));
            curRoom = null;
            curStart = null;
          }
          d = d.add(1, "day");
          continue;
        }
        if (!curRoom) {
          curRoom = roomForDay;
          curStart = d.clone();
        } else if (curRoom.roomId !== roomForDay.roomId) {
          parts.push(buildSegmentForRoom(curRoom, curStart, d));
          curRoom = roomForDay;
          curStart = d.clone();
        }
        d = d.add(1, "day");
      }
      if (curRoom && curStart) {
        parts.push(buildSegmentForRoom(curRoom, curStart, b.end));
      }
    }

    if (!parts.length) {
      notify.warning("No availability found for the selected room/type in this window.");
      dgroupEnd();
      return;
    }

    const totalNights = parts.reduce((n, p) => n + (p.nights || 0), 0);
    const currency = parts[0]?.currency || "BWP";
    const totalPrice = parts.reduce((s, p) => s + (Number(p.segmentPrice) || 0), 0);

    dtable(
      parts.map((p, i) => ({
        "#": i + 1,
        Room: p.roomNumber,
        RoomId: p.roomId,
        From: p.startISO,
        To: p.endISO,
        Nights: p.nights,
        SegPrice: p.segmentPrice,
      })),
      "Composite parts"
    );
    dgroupEnd();

    const composite = {
      kind: "composite",
      startISO: winStart.format("YYYY-MM-DD"),
      endISO: winEnd.format("YYYY-MM-DD"),
      roomTypeId: getRoomTypeId(selectedRoom),
      roomTypeName: getTypeName(selectedRoom) || "",
      currency,
      nights: totalNights,
      segmentPrice: totalPrice,
      parts: parts.sort(
        (a, b) => dayjs(a.startISO).valueOf() - dayjs(b.startISO).valueOf()
      ),
    };

    setSegments((prev) => {
      const dup = prev.find(
        (s) =>
          s.kind === "composite" &&
          s.startISO === composite.startISO &&
          s.endISO === composite.endISO &&
          s.roomTypeId === composite.roomTypeId
      );
      if (dup) return prev;
      const out = [...prev, composite];
      out.sort(
        (a, b) =>
          dayjs((a.startISO || a?.parts?.[0]?.startISO)).valueOf() -
          dayjs((b.startISO || b?.parts?.[0]?.startISO)).valueOf()
      );
      return out;
    });

    setSelectedRange([]);
    notify.success(`Added stay • ${totalNights} night(s) • ${parts.length} room(s)`);
  };

  const addSegment = () => {
    const seg = buildSegmentFromSelection();
    if (!seg) return;

    dgroup("➕ addSegment()");
    dtable([{
      Room: seg.roomNumber,
      RoomId: seg.roomId,
      From: seg.startISO,
      To: seg.endISO,
      Nights: seg.nights,
      PricePerNight: seg.pricePerNight,
      DiscountPct: seg.discount,
      VATpct: seg.vatPercentage,
      Levies: seg.additionalLevies,
      SegPrice: seg.segmentPrice,
    }]);
    dgroupEnd();

    setSegments((prev) => {
      const exists = prev.some(
        (s) =>
          (s.roomId || s?.parts?.[0]?.roomId) === seg.roomId &&
          (s.startISO || s?.parts?.[0]?.startISO) === seg.startISO &&
          (s.endISO || s?.parts?.[0]?.endISO) === seg.endISO
      );
      return exists
        ? prev
        : [...prev, seg].sort(
            (a, b) =>
              dayjs((a.startISO || a?.parts?.[0]?.startISO)).valueOf() -
              dayjs((b.startISO || b?.parts?.[0]?.startISO)).valueOf()
          );
    });
    setSelectedRange([]);
    notify.success(`Added segment • Room ${seg.roomNumber} • ${seg.nights} night(s)`);
    if (allowSupplement && selectedRoom?.conflictingDates?.length) {
      setTimeout(() => autoFillConflicts(), 0);
    }
  };

const dateIsInAvailableSegment = (date) => {
  if (!dates?.[0] || !dates?.[1]) return { status: "unavailable", seg: null };

  // ✅ while loading, don't mark as booked
  if (loading.availability && (!roomAvailability || roomAvailability.length === 0)) {
    return { status: "unavailable", seg: null };
  }

  const d = dayjs(date).startOf("day");
  const reqStart = dayjs(dates[0]).startOf("day");
  const reqEnd   = dayjs(dates[1]).startOf("day"); // checkout day

  if (d.isBefore(reqStart, "day") || !d.isBefore(reqEnd, "day")) {
    return { status: "unavailable", seg: null };
  }

  const seg = (roomAvailability || []).find((s) => dayInSeg(d, s));
  if (!seg) return { status: "booked", seg: null };

  return { status: seg.status === "Available" ? "available" : "booked", seg };
};



  const onCalendarSelect = (date) => {
  if (!selectedRoom) return;

  const target = dayjs(date).startOf("day");
  const { status, seg } = dateIsInAvailableSegment(target);

  // only allow click on green
  if (status !== "available" || !seg) {
    setSelectedRange([]);
    return;
  }

  const reqStart = dayjs(dates?.[0]).startOf("day");
  const reqEnd   = dayjs(dates?.[1]).startOf("day"); // checkout (exclusive for nights)

  const segStart = dayjs(seg.start).startOf("day");
  const segEnd   = dayjs(seg.end).startOf("day"); // exclusive

  const start = segStart.isAfter(reqStart) ? segStart : reqStart;
  const end   = segEnd.isBefore(reqEnd) ? segEnd : reqEnd;

  if (!end.isAfter(start, "day")) {
    setSelectedRange([]);
    return;
  }

  const piece = buildSegmentForRoom(selectedRoom, start, end);

  // THIS is the missing part: commit immediately
  addPiecesNoOverlap([piece]);

  // optional: keep temp range for the alert, or clear it
  setSelectedRange([piece.startISO, piece.endISO]);

  notify.success(
    `Added Room ${selectedRoom.roomNumber} • ${piece.startISO} → ${piece.endISO} • ${piece.nights} night(s)`
  );

  if (allowSupplement && selectedRoom?.conflictingDates?.length) {
    setTimeout(() => autoFillConflicts(), 0);
  }
};


  const currencyForTop = useMemo(() => {
    if (segments.length) {
      return (
        segments[0]?.currency ||
        segments[0]?.parts?.[0]?.currency ||
        "BWP"
      );
    }
    return selectedRoom?.currency || "BWP";
  }, [segments, selectedRoom]);

  const priceRows = useMemo(() => {
    const rows = [];

    for (let i = 0; i < segments.length; i++) {
      const s = segments[i];
      const pieces =
        s.parts && Array.isArray(s.parts) && s.parts.length ? s.parts : [s];

      let n = 0, b = 0, v = 0, l = 0, dsc = 0, amt = 0;

      for (const p of pieces) {
        const nights = p.nights || nightsBetween(p.startISO, p.endISO) || 0;
        const baseAfter =
          Number(p.pricePerNight || 0) *
          (1 - Number(p.discount || 0) / 100);
        const vatAmt = baseAfter * (Number(p.vatPercentage || 0) / 100);
        const nightly = baseAfter + vatAmt + Number(p.additionalLevies || 0);
        const segTotal = Number.isFinite(p.segmentPrice)
          ? Number(p.segmentPrice)
          : nightly * nights;

        const preBase = Number(p.pricePerNight || 0) * nights;
        const basePart = baseAfter * nights;
        const vatPart = vatAmt * nights;
        const levPart = Number(p.additionalLevies || 0) * nights;
        const segDiscount = Math.max(preBase - basePart, 0);

        n += nights;
        b += basePart;
        v += vatPart;
        l += levPart;
        dsc += segDiscount;
        amt += segTotal;
      }

      const labelRoom =
        pieces.length === 1
          ? `Room ${pieces[0].roomNumber}`
          : `Room ${pieces[0].roomNumber} + ${pieces.length - 1} more`;
      const typeName = s.roomTypeName || getTypeName(pieces[0]) || "Type";

      rows.push({
        key: `seg-${i}`,
        index: i + 1,
        room: labelRoom,
        type: typeName,
        dates: `${dayjs(s.startISO || pieces[0].startISO).format(
          "ddd, MMM D"
        )} → ${dayjs(s.endISO || pieces[pieces.length - 1].endISO).format(
          "ddd, MMM D"
        )}`,
        nights: n,
        base: b,
        vat: v,
        lev: l,
        discount: dsc,
        amount: amt,
        currency: s.currency || pieces[0]?.currency || "BWP",
      });
    }

    if (!rows.length && selectedRoom && selectedRange.length === 2) {
      const n = nightsBetween(selectedRange[0], selectedRange[1]);
      const p = perNightFromRoom(selectedRoom);
      const base = p.base * n;
      const vat = p.vat * n;
      const lev = p.lev * n;
      const amount = p.total * n;
      const preBase = num(selectedRoom.price) * n;
      const discount = Math.max(preBase - base, 0);

      rows.push({
        key: "single",
        index: 1,
        room: `Room ${selectedRoom.roomNumber}`,
        type: getTypeName(selectedRoom) || "Type",
        dates: `${dayjs(selectedRange[0]).format("ddd, MMM D")} → ${dayjs(
          selectedRange[1]
        ).format("ddd, MMM D")}`,
        nights: n,
        base,
        vat,
        lev,
        discount,
        amount,
        currency: selectedRoom.currency || "BWP",
      });
    }

    return rows;
  }, [segments, selectedRoom, selectedRange]);

  const totals = useMemo(() => {
    return priceRows.reduce(
      (a, r) => ({
        nights: (a.nights || 0) + (r.nights || 0),
        base: (a.base || 0) + (r.base || 0),
        vat: (a.vat || 0) + (r.vat || 0),
        lev: (a.lev || 0) + (r.lev || 0),
        discount: (a.discount || 0) + (r.discount || 0),
        amount: (a.amount || 0) + (r.amount || 0),
      }),
      {}
    );
  }, [priceRows]);

  // --------- Guest readiness -----------
const firstName = Form.useWatch("firstName", form);
const guestId = Form.useWatch("guestId", form);
const idNumber = Form.useWatch("idNumber", form);
const lastName = Form.useWatch("lastName", form);
const email = Form.useWatch("email", form);

const postalAddress = Form.useWatch("postalAddress", form);
const residentialAddress = Form.useWatch("residentialAddress", form);

const numberOfAdults = Form.useWatch("numberOfAdults", form);
const dateOfBirth = Form.useWatch("dateOfBirth", form);

const guestReady = !!(
  (
    (idNumber?.trim()) &&
    (firstName?.trim()) &&
    (lastName?.trim()) &&
    (email?.trim()) &&
    (postalAddress?.trim()) &&
    (residentialAddress?.trim()) &&
    numberOfAdults &&
    dateOfBirth
  ) ||
  guestId // still allowed, but remember: backend currently still enforces addresses unless you supply/fetch them
);

// Enhanced debug logging for guest readiness
useEffect(() => {
  if (DEBUG) {
    dgroup("👤 Guest Readiness Check");
    dlog("guestReady:", guestReady);
    dlog("Fields:", {
      guestId,
      idNumber,
      firstName,
      lastName,
      email,
      postalAddress,
      residentialAddress,
      numberOfAdults,
      dateOfBirth,
    });
    dgroupEnd();
  }
}, [
  guestReady,
  guestId,
  idNumber,
  firstName,
  lastName,
  email,
  postalAddress,
  residentialAddress,
  numberOfAdults,
  dateOfBirth,
]);


  const selectionReady =
    segments.length > 0 || (selectedRoom && selectedRange.length === 2);

  const canConfirm = guestReady && selectionReady && !currencyMismatch;

  // ===== Guest identity resolution with verification =====
 const safePick = (o, ...keys) => { 
  for (const k of keys) if (o?.[k] != null && o[k] !== "") return o[k]; 
  return null;
};
 const fetchGuestById = async (guestId, hotelId) => {
  try {
    const { data } = await axiosInstance.get(`/Guests/${guestId}`, { 
      params: { hotelId },
      timeout: 5000
    });
    return data || {};
  } catch (error) {
    console.warn(`Failed to fetch guest ${guestId}:`, error);
    return {};
  }
};

 const resolveGuestFields = async (v, hotelId) => {
  const addr = (v.address || "").trim();

  // Prefer explicit fields, fallback to generic "address" if you still have it
  const postal = (v.postalAddress || addr || "").trim();
  const residential = (v.residentialAddress || addr || "").trim();

  const base = {
    mobileNumber: v.mobileNumber || "",
    phoneNumber: v.phoneNumber || v.mobileNumber || "",

    // ✅ REQUIRED by backend
    postalAddress: postal,
    residentialAddress: residential,

    // optional legacy/back-compat
    address: addr,

    numberOfAdults: parseInt(v.numberOfAdults, 10) || 1,
    includesKids: !!v.includesKids,
    numberOfKids: parseInt(v.numberOfKids, 10) || 0,
    remarks: v.remarks || "",
    notes: v.notes || "",

    companyName: v.companyName || "",
    companyEmail: v.companyEmail || "",
  };

  dgroup("👤 resolveGuestFields()");
  dlog("Input addresses:", {
    postalAddress: v.postalAddress,
    residentialAddress: v.residentialAddress,
    address: v.address,
  });

  // If guestId provided, verify it belongs to this hotel
  if (v.guestId) {
    const fetched = await fetchGuestById(v.guestId, hotelId);
    const fetchedHotelId = fetched?.hotelId ?? fetched?.HotelId;
    const belongs = Number(fetchedHotelId) === Number(hotelId);

    if (belongs) {
      const fetchedPostal =
        safePick(fetched, "postalAddress", "PostalAddress", "address", "Address") || "";
      const fetchedResidential =
        safePick(fetched, "residentialAddress", "ResidentialAddress") ||
        safePick(fetched, "postalAddress", "PostalAddress", "address", "Address") ||
        "";

      const resolved = {
        guestId: parseInt(v.guestId, 10),
        idNumber: v.idNumber || safePick(fetched, "idNumber", "IdNumber", "id_no") || "",
        firstName: v.firstName || safePick(fetched, "firstName", "FirstName") || "",
        lastName: v.lastName || safePick(fetched, "lastName", "LastName") || "",
        email: v.email || safePick(fetched, "email", "Email") || "",
        dateOfBirth: v.dateOfBirth
          ? dayjs(v.dateOfBirth).format("YYYY-MM-DD")
          : (safePick(fetched, "dateOfBirth", "DateOfBirth", "dob")
              ? dayjs(safePick(fetched, "dateOfBirth", "DateOfBirth", "dob")).format("YYYY-MM-DD")
              : "1990-01-01"),

        // ✅ addresses (form overrides fetched)
        postalAddress: (postal || String(fetchedPostal).trim() || ""),
        residentialAddress: (residential || String(fetchedResidential).trim() || ""),

        ...base,
      };

      dlog("Resolved guest (existing):", {
        guestId: resolved.guestId,
        postalAddress: resolved.postalAddress,
        residentialAddress: resolved.residentialAddress,
      });
      dgroupEnd();
      return resolved;
    }
  }

  // New guest (or guestId invalid)
  const resolved = {
    ...base,
    idNumber: (v.idNumber || "").trim(),
    firstName: (v.firstName || "").trim(),
    lastName: (v.lastName || "").trim(),
    email: (v.email || "").trim(),
    dateOfBirth: v.dateOfBirth ? dayjs(v.dateOfBirth).format("YYYY-MM-DD") : "1990-01-01",
  };

  dlog("Resolved guest (new):", {
    postalAddress: resolved.postalAddress,
    residentialAddress: resolved.residentialAddress,
  });
  dgroupEnd();
  return resolved;
};


  // --- Room lookup/normalization helpers -------------------------------------
  const pick = (o, ...keys) => { for (const k of keys) if (o && o[k] != null) return o[k]; };

  function buildRoomIndex(availableRooms, selectedRoom) {
    const byId = new Map();
    const byNo = new Map();
    const add = (r) => {
      if (!r) return;
      const id = r.roomId ?? r.RoomId;
      const no = r.roomNumber ?? r.RoomNumber;
      const typeId =
        r.roomTypeId ?? r.RoomTypeId ?? r?.roomType?.id ?? r?.RoomType?.Id ?? null;
      if (id != null) byId.set(String(id), { roomId: Number(id), roomNumber: no, roomTypeId: Number(typeId) || null });
      if (no != null) byNo.set(String(no), { roomId: Number(id), roomNumber: String(no), roomTypeId: Number(typeId) || null });
    };
    (availableRooms || []).forEach(add);
    add(selectedRoom);
    return { byId, byNo };
  }

  async function getRoomByIdStrict(roomId, hotelId) {
    const { data } = await axiosInstance.get(`/Rooms/${roomId}`, { params: { hotelId } });
    const id = data?.roomId ?? data?.RoomId;
    const no = data?.roomNumber ?? data?.RoomNumber;
    const typeId = data?.roomTypeId ?? data?.RoomTypeId ?? data?.roomType?.id ?? data?.RoomType?.Id;
    return { roomId: Number(id), roomNumber: no, roomTypeId: Number(typeId) || null };
  }

  /** Ensure each piece has a valid (roomId, roomTypeId) confirmed on server; prefer local index first */
  async function resolvePiecesWithServer(pieces, hotelId, availableRooms, selectedRoom) {
    const idx = buildRoomIndex(availableRooms, selectedRoom);
    const out = [];
    for (const p of pieces) {
      let resolved = null;

      // 1) Local index by id
      if (p.roomId != null) {
        const hit = idx.byId.get(String(p.roomId));
        if (hit?.roomId && hit?.roomTypeId) {
          resolved = { ...p, roomId: hit.roomId, roomTypeId: hit.roomTypeId, roomNumber: p.roomNumber ?? hit.roomNumber };
        }
      }

      // 2) Local index by roomNumber
      if (!resolved && p.roomNumber != null) {
        const hit = idx.byNo.get(String(p.roomNumber));
        if (hit?.roomId && hit?.roomTypeId) {
          resolved = { ...p, roomId: hit.roomId, roomTypeId: hit.roomTypeId, roomNumber: hit.roomNumber };
        }
      }

      // 3) Server lookup by id
      if (!resolved && p.roomId != null) {
        try {
          const rd = await getRoomByIdStrict(p.roomId, hotelId);
          if (rd?.roomId && rd?.roomTypeId) {
            resolved = { ...p, roomId: rd.roomId, roomTypeId: rd.roomTypeId, roomNumber: p.roomNumber ?? rd.roomNumber };
          }
        } catch { /* ignore */ }
      }

      if (!resolved || !resolved.roomId || !resolved.roomTypeId) {
        const label = p.roomNumber ?? p.roomId ?? "unknown";
        throw new Error(`Room reference invalid for segment ${label} (hotel ${hotelId})`);
      }
      out.push(resolved);
    }
    return out;
  }

  function deriveHeaderFromPieces(pieces) {
    const sorted = [...pieces].sort((a, b) => a.startISO.localeCompare(b.startISO));
    const first = sorted[0];
    return {
      headerRoomId: parseInt(first.roomId, 10),
      headerRoomTypeId: parseInt(first.roomTypeId, 10),
      overallIn: dayjs.min(...pieces.map((p) => dayjs(p.startISO))).format("YYYY-MM-DD"),
      overallOut: dayjs.max(...pieces.map((p) => dayjs(p.endISO))).format("YYYY-MM-DD"),
    };
  }

  function validatePiecesContinuous(pieces) {
    if (!pieces.length) return { ok: true };
    const sorted = [...pieces].sort((a, b) => a.startISO.localeCompare(b.startISO));
    for (let i = 0; i < sorted.length; i++) {
      const p = sorted[i];
      if (!dayjs(p.endISO).isAfter(dayjs(p.startISO), "day")) {
        return { ok: false, msg: `Segment ${i + 1} has non-positive duration.` };
      }
      if (i > 0) {
        const prev = sorted[i - 1];
        // allow contiguous (prev.end == current.start), disallow overlap or gaps if you require full cover
        if (dayjs(p.startISO).isBefore(dayjs(prev.endISO), "day")) {
          return { ok: false, msg: `Segments ${i} and ${i + 1} overlap.` };
        }
      }
    }
    return { ok: true };
  }

  function withinEnvelope(pieces, inIso, outIso) {
    const A = dayjs(inIso).startOf("day");
    const B = dayjs(outIso).startOf("day");
    return pieces.every((p) => {
      const s = dayjs(p.startISO).startOf("day");
      const e = dayjs(p.endISO).startOf("day");
      return !s.isBefore(A, "day") && !e.isAfter(B, "day");
    });
  }


  // Only flag overlaps *within the same room* (checkout is exclusive)
 function validatePiecesNoOverlapPerRoom(pieces) {
  if (!pieces?.length) return { ok: true };

  // 1) sanity (end > start; checkout-exclusive)
  for (let i = 0; i < pieces.length; i++) {
    const p = pieces[i];
    if (!(p?.startISO && p?.endISO)) {
      return { ok: false, msg: `Segment ${i + 1} missing dates.` };
    }
    if (!(p.endISO > p.startISO)) {
      return { ok: false, msg: `Segment ${i + 1} has non-positive duration.` };
    }
  }

  // 2) group by room (use roomId first, fallback to roomNumber)
  const keyOf = (p) => String(p.roomId ?? p.roomNumber ?? "");
  const byRoom = new Map();
  for (const p of pieces) {
    const k = keyOf(p);
    if (!byRoom.has(k)) byRoom.set(k, []);
    byRoom.get(k).push(p);
  }

  // 3) only forbid overlaps **within the same room**
  for (const [key, arr] of byRoom) {
    const sorted = arr.slice().sort((a, b) => a.startISO.localeCompare(b.startISO));
    for (let i = 1; i < sorted.length; i++) {
      // overlap if next.start < prev.end (end is checkout-exclusive)
      if (sorted[i].startISO < sorted[i - 1].endISO) {
        const rn = sorted[i].roomNumber ?? sorted[i].roomId ?? key;
        const a1 = `${sorted[i - 1].startISO}→${sorted[i - 1].endISO}`;
        const a2 = `${sorted[i].startISO}→${sorted[i].endISO}`;
        return { ok: false, msg: `Room ${rn}: segments ${a1} and ${a2} overlap.` };
      }
    }
  }
  return { ok: true };
}

const addRoomForFullRange = (room) => {
  if (!dates?.[0] || !dates?.[1]) {
    notify.warning("Pick the global date range first.");
    return;
  }
  const start = dates[0].startOf("day");
  const end   = dates[1].startOf("day");
  if (!end.isAfter(start, "day")) {
    notify.warning("Check-out must be after check-in");
    return;
  }

  const seg = buildSegmentForRoom(room, start, end); // you already have this helper

  // de-dupe
  const key = `${seg.roomId}|${seg.startISO}|${seg.endISO}`;
  const exists = flattenPieces(segments).some(p => `${p.roomId}|${p.startISO}|${p.endISO}` === key);
  if (exists) {
    notify.info("That exact segment is already added.");
    return;
  }

  setSegments(prev => [...prev, seg].sort(
    (a,b) => dayjs((a.startISO||a?.parts?.[0]?.startISO)).valueOf()
          - dayjs((b.startISO||b?.parts?.[0]?.startISO)).valueOf()
  ));

  // make the calendar reflect what you just added
  setSelectedRoom(room);
  setSelectedRange([seg.startISO, seg.endISO]);

  notify.success(`Added Room ${room.roomNumber} • ${seg.nights} night(s)`);
};


  // ---------- Submit booking ----------
 const handleSubmit = async () => {
  try {
    setLoading((s) => ({ ...s, submitting: true }));
    const v = form.getFieldsValue(true);

    if (!dates?.[0] || !dates?.[1]) throw new Error("Please select both check-in and check-out dates.");
    if (!(segments.length > 0 || (selectedRoom && selectedRange.length === 2)))
      throw new Error("Select a room and a date segment (or add segments).");
    if (currencyMismatch) throw new Error("All segments must use the same currency.");

    const envelopeIn = dayjs(dates[0]).format("YYYY-MM-DD");
    const envelopeOut = dayjs(dates[1]).format("YYYY-MM-DD");

    // Resolve guest fields (ALWAYS include identity fields)
    const guest = await resolveGuestFields(v, authData.hotelId);
    
    // Validate required guest fields (regardless of existing or new guest)
    const requiredFields = ['firstName', 'lastName', 'email', 'idNumber', 'dateOfBirth', 'postalAddress', 'residentialAddress'];
    const missingFields = requiredFields.filter(field => !guest[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required guest fields: ${missingFields.join(', ')}`);
    }

    // Age gate (must be >= 18 on the first segment's check-in)
     const chkIn = segments.length > 0
  ? dayjs.min(...flattenPieces(segments).map(p => dayjs(p.startISO)))
  : dayjs(selectedRange?.[0]);

    if (chkIn && guest?.dateOfBirth) {
      const age = chkIn.diff(dayjs(guest.dateOfBirth), 'year');
      
      // More precise age calculation
      const birthDate = dayjs(guest.dateOfBirth);
      const isAdult = chkIn.diff(birthDate, 'year') >= 18;
      
      if (!isAdult) {
        const exactAge = chkIn.diff(birthDate, 'year', true).toFixed(2);
        throw new Error(`Guest must be at least 18 by check-in date. Current age: ${exactAge} years.`);
      }
      
      dlog("Age validation:", {
        birthDate: birthDate.format('YYYY-MM-DD'),
        checkIn: chkIn.format('YYYY-MM-DD'),
        ageYears: chkIn.diff(birthDate, 'year'),
        isAdult
      });
    }

    let payload;

    if (segments.length === 0) {
      // ===== Single segment (simple stay) =====
      const n = nightsBetween(selectedRange[0], selectedRange[1]) || 1;
      const p = perNightFromRoom(selectedRoom);
      const grandTotal = p.total * n;

      const pieceRaw = {
        roomId: selectedRoom.roomId,
        roomNumber: selectedRoom.roomNumber,
        startISO: dayjs(selectedRange[0]).format("YYYY-MM-DD"),
        endISO: dayjs(selectedRange[1]).format("YYYY-MM-DD"),
        segmentPrice: grandTotal,
      };

      const normPieces = await resolvePiecesWithServer([pieceRaw], authData.hotelId, availableRooms, selectedRoom);

      // sanity checks
      const ov = validatePiecesNoOverlapPerRoom(normPieces);
      if (!ov.ok) throw new Error(ov.msg);

      if (!withinEnvelope(normPieces, envelopeIn, envelopeOut)) {
        throw new Error("Selected segment is outside the chosen date range.");
      }

      const { headerRoomId, headerRoomTypeId, overallIn, overallOut } = deriveHeaderFromPieces(normPieces);

      const roomBookings = [
        {
          roomId: headerRoomId,
          roomNumber: normPieces[0].roomNumber,
          guestName: `${guest.firstName || ""} ${guest.lastName || ""}`.trim(),
          bookingStatus: "Confirmed",
          checkInDate: overallIn,
          checkOutDate: overallOut,
        },
      ];

      // Build payload - ALWAYS include all guest identity fields
      const basePayload = {
        ...guest, // This now includes ALL fields including guestId if verified
        hotelId: parseInt(authData.hotelId, 10),
        roomId: headerRoomId,
        roomTypeId: headerRoomTypeId,
        checkInDate: overallIn,
        checkOutDate: overallOut,
        totalAmount: Number.isFinite(grandTotal) ? Number(grandTotal) : 0,
        paidAmount: Number(v.paidAmount) || 0,
        discount: 0,
        roomBookings,
        roomSegments: [
          {
            roomId: headerRoomId,
            checkInDate: overallIn,
            checkOutDate: overallOut,
            segmentPrice: Number(normPieces[0].segmentPrice) || 0,
          },
        ],
      };
      
      // Note: We no longer delete guestId - backend will handle existing guest logic
      payload = basePayload;
    } else {
      // ===== Split stay / composite =====
      const piecesRaw = flattenPieces(segments).map((p) => ({
        roomId: p.roomId,
        roomNumber: p.roomNumber,
        startISO: p.startISO,
        endISO: p.endISO,
        segmentPrice: Number(p.segmentPrice) || 0,
      }));

      const normPieces = await resolvePiecesWithServer(piecesRaw, authData.hotelId, availableRooms, selectedRoom);

      // sanity checks
      const ov = validatePiecesNoOverlapPerRoom(normPieces);
      if (!ov.ok) throw new Error(ov.msg);
      if (!withinEnvelope(normPieces, envelopeIn, envelopeOut)) {
        throw new Error("One or more segments are outside the selected date window.");
      }

      const totalFromSegments = normPieces.reduce((s, p) => s + (Number(p.segmentPrice) || 0), 0);
      const { headerRoomId, headerRoomTypeId, overallIn, overallOut } = deriveHeaderFromPieces(normPieces);

      const roomBookings = normPieces
        .map((p) => ({
          roomId: parseInt(p.roomId, 10),
          roomNumber: p.roomNumber,
          guestName: `${guest.firstName || ""} ${guest.lastName || ""}`.trim(),
          bookingStatus: "Confirmed",
          checkInDate: dayjs(p.startISO).format("YYYY-MM-DD"),
          checkOutDate: dayjs(p.endISO).format("YYYY-MM-DD"),
        }))
        .sort((a, b) => a.checkInDate.localeCompare(b.checkInDate));

      // Build payload - ALWAYS include all guest identity fields
      const basePayload = {
        ...guest, // This now includes ALL fields including guestId if verified
        hotelId: parseInt(authData.hotelId, 10),
        checkInDate: overallIn,
        checkOutDate: overallOut,
        roomId: headerRoomId,
        roomTypeId: headerRoomTypeId,
        totalAmount: Number(totalFromSegments) || 0,
        paidAmount: Number(v.paidAmount) || 0,
        discount: 0,
        roomBookings,
        roomSegments: normPieces.map((p) => ({
          roomId: parseInt(p.roomId, 10),
          checkInDate: dayjs(p.startISO).format("YYYY-MM-DD"),
          checkOutDate: dayjs(p.endISO).format("YYYY-MM-DD"),
          segmentPrice: Number(p.segmentPrice) || 0,
        })),
      };
      
      // Note: We no longer delete guestId - backend will handle existing guest logic
      payload = basePayload;
    }

    // ---- DEBUG SUBMIT PAYLOAD ----
    dgroup("📦 SUBMIT PAYLOAD (normalized)");
    dlog("Header:", {
      hotelId: payload.hotelId,
      checkInDate: payload.checkInDate,
      checkOutDate: payload.checkOutDate,
      roomId: payload.roomId,
      roomTypeId: payload.roomTypeId,
      totalAmount: payload.totalAmount,
      paidAmount: payload.paidAmount,
    });
    dlog("Guest:", {
      guestId: payload.guestId,
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      idNumber: payload.idNumber,
      dateOfBirth: payload.dateOfBirth,
      mobileNumber: payload.mobileNumber,
      phoneNumber: payload.phoneNumber,
      address: payload.address,
    });
    dlog("Additional:", {
      numberOfAdults: payload.numberOfAdults,
      includesKids: payload.includesKids,
      numberOfKids: payload.numberOfKids,
      remarks: payload.remarks,
      notes: payload.notes,
    });
    if (payload.roomSegments) {
      dlog("roomSegments");
      console.table(
        payload.roomSegments.map((rs, i) => ({
          "#": i + 1,
          RoomId: rs.roomId,
          From: rs.checkInDate,
          To: rs.checkOutDate,
          SegmentPrice: rs.segmentPrice,
        }))
      );
    }
    if (payload.roomBookings) {
      dlog("roomBookings");
      console.table(
        payload.roomBookings.map((rb, i) => ({
          "#": i + 1,
          RoomId: rb.roomId,
          RoomNumber: rb.roomNumber,
          From: rb.checkInDate,
          To: rb.checkOutDate,
        }))
      );
    }
    dgroupEnd();
    // -------------------------------

    const res = await axiosInstance.post("/Bookings", payload);
    const data = res?.data || {};

    Modal.success({
      title: segments.length > 0 ? "Booking Confirmed (Split Stay)" : "Booking Confirmed",
      content: `Booking #${data.bookingId ?? data.BookingId ?? ""} created successfully!`,
      onOk: () => {
        form.resetFields();
        setSelectedRoom(null);
        setAvailableRooms([]);
        setDates([null, null]);
        setRoomAvailability([]);
        setSegments([]);
        setSelectedRange([]);
        navigate("/hotels/bookinglistpage");
      },
    });
  } catch (e) {
    console.error("Booking error:", e);
    const problem = e.response?.data;

    // Enhanced error logging
    dgroup("❌ BOOKING ERROR");
    dlog("Error:", e);
    dlog("Response:", problem);
    dgroupEnd();

    // Show DB/FK hints for common cases
    const maybeDb = problem?.code === "DB_ERROR" || e.response?.status === 400;
    if (maybeDb) {
      notify.error(
        problem?.message ||
          "Failed to create booking. Check that the selected guest belongs to this hotel and that segments do not overlap.",
        10
      );
      return;
    }

    if (e.response?.status === 400 && problem) {
      const lines = [];
      if (problem.errors && typeof problem.errors === "object") {
        for (const [k, val] of Object.entries(problem.errors)) {
          lines.push(`${k || "(general)"}: ${Array.isArray(val) ? val.join(", ") : val}`);
        }
      }
      if (problem.message) lines.push(problem.message);
      if (problem.title && !lines.length) lines.push(problem.title);
      if (problem.detail) lines.push(problem.detail);
      notify.error(lines.join(" | ") || "Invalid booking data", 10);
    } else {
      notify.error(e.message || "Booking failed. Please try again.", 8);
    }
  } finally {
    setLoading((s) => ({ ...s, submitting: false }));
  }
};


  // Price popover builder for room list
  const nightlyTag = (record) => {
    const baseAfter =
      (record.price || 0) * (1 - (record.discount || 0) / 100);
    const vat = baseAfter * ((record.vatPercentage || 0) / 100);
    const lev = record.additionalLevies || 0;
    const nightly = baseAfter + vat + lev;
    const currency = record.currency || "BWP";
    return (
      <Popover
        title="Price Details"
        content={
          <>
            <div>
              Base before discount: {currency} {fmt.format(record.price || 0)}
            </div>
            {record.discount > 0 && <div>Discount: {record.discount}%</div>}
            <div>
              Base after discount: {currency} {fmt.format(baseAfter)}
            </div>
            {Number(record.vatPercentage) > 0 && (
              <div>
                VAT ({record.vatPercentage}%): {currency}{" "}
                {fmt.format(vat)}
              </div>
            )}
            <div>
              Levies: {currency} {fmt.format(lev)}
            </div>
            <Divider style={{ margin: "8px 0" }} />
            <div>
              <strong>
                Per night: {currency} {fmt.format(nightly)}
              </strong>
            </div>
          </>
        }
      >
        <Tag className={record.discount ? "bk-price is-discount" : "bk-price"} style={{ margin: 0 }}>
          {currency} {fmt.format(nightly)}
        </Tag>
      </Popover>
    );
  };

  const guestCtrl = guestReady ? (
    <Tooltip title="Edit guest">
      <Tag
        icon={<UserOutlined />}
        className="bk-guest"
        onClick={() => setGuestDrawerOpen(true)}
        style={{ cursor: "pointer", marginLeft: 8 }}
      >
        {`Guest: ${(`${firstName || ""} ${lastName || ""}`).trim()}`}
        {idNumber ? ` • ${idNumber}` : ""}
      </Tag>
    </Tooltip>
  ) :  null; 

  return (
    <div className="hms-page booking-page">
      {/* Provide a form context so useWatch works even if fields live in GuestDrawer */}
      <Form form={form} component={false} />

      {/* One-liner: Date | Price detail | Actions */}
      <Affix offsetTop={68}>
        <Card size="small" className="hms-card" style={{ marginBottom: 12 }}>
          <Row gutter={8} align="middle" wrap={false}>
            {/* Date */}
            <Col flex="420px">
              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <Text type="secondary">Date</Text>
                <RangePicker
                  size="small"
                  style={{ width: "100%" }}
                  value={
                    dates?.[0] && dates?.[1] ? [dates[0], dates[1]] : undefined
                  }
                  onChange={onDatesChange}
                  disabledDate={(current) =>
                    current && current < dayjs().startOf("day")
                  }
                  format="YYYY-MM-DD"
                />
              </Space>
            </Col>

            {/* Price detail (tiny) */}
            <Col flex="auto">
              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <Text type="secondary">Price detail</Text>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {(() => {
                    const grandCurrency = currencyForTop;
                    const nSegments =
                      segments.length || (selectedRange.length === 2 ? 1 : 0);
                    const nights = totals.nights || 0;
                    const totalAmt = totals.amount || 0;
                    const popContent = (
                      <>
                        <div>
                          Base: {grandCurrency} {fmt.format(totals.base || 0)}
                        </div>
                        {(totals.vat || 0) > 0 && (
                          <div>
                            VAT: {grandCurrency} {fmt.format(totals.vat || 0)}
                          </div>
                        )}
                        <div>
                          Levies: {grandCurrency} {fmt.format(totals.lev || 0)}
                        </div>
                        <div>
                          Discount: -{grandCurrency}{" "}
                          {fmt.format(totals.discount || 0)}
                        </div>
                        <Divider style={{ margin: "8px 0" }} />
                        <div>
                          <strong>
                            Total: {grandCurrency} {fmt.format(totalAmt)}
                          </strong>
                        </div>
                        {nSegments > 1 && (
                          <div style={{ marginTop: 6 }}>
                            {nSegments} segments
                          </div>
                        )}
                        {nights > 0 && <div>{nights} night(s)</div>}
                      </>
                    );
                    return (
                      <>
                        <Popover title="Price detail" content={popContent}>
                          <Tag
                            className="bk-total"
                            style={{
                              margin: 0,
                              fontSize: 13,
                              lineHeight: "22px",
                            }}
                          >
                            {grandCurrency} {fmt.format(totalAmt)}
                            {nights ? ` • ${nights} night(s)` : ""}
                            {nSegments > 1 ? ` • ${nSegments} segments` : ""}
                          </Tag>
                        </Popover>

                        {(priceRows?.length ?? 0) > 0 && (
                          <Tooltip title="Breakdown">
                            <Button
                              size="small"
                              type="text"
                              icon={<FileTextOutlined />}
                              onClick={() => setBreakdownOpen(true)}
                              style={{ paddingInline: 6 }}
                            />
                          </Tooltip>
                        )}
                        {/* segments rooms tag */}
                        {roomsTagNode}
                        {/*guestChip */}
                        {guestCtrl}
                      </>
                    );
                  })()}
                </div>
              </Space>
            </Col>

            {/* Actions */}
            <Col flex="420px" style={{ textAlign: "right" }}>
              <Space style={{ marginTop: 20 }} size={8} wrap>
                <Tooltip title="Add guest details">
                  <Button
                    size="small"
                    type="primary"
                    icon={<UserAddOutlined />}
                    onClick={() => setGuestDrawerOpen(true)}
                  >
                    Add Guest 
                  </Button>
                </Tooltip>

                <Tooltip title="Build one stay across rooms based on the Date range">
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={addCompositeFromDateRange}
                    disabled={!selectedRoom || !dates?.[0] || !dates?.[1]}
                  >
                    Add Segment (Date Range)
                  </Button>
                </Tooltip>

                <Tooltip
                  title={
                    canConfirm
                      ? "Create the booking"
                      : "Add guest and select stay first"
                  }
                >
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={handleSubmit}
                    disabled={!canConfirm}
                    loading={loading.submitting}
                  >
                    Confirm Booking
                  </Button>
                </Tooltip>

                <Tooltip title="Clear all selections and reset">
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      const checkIn = dayjs().add(1, "day").startOf("day");
                      const checkOut = checkIn.add(2, "day");

                      setDates([checkIn, checkOut]);
                      setAvailableRooms([]);
                      setSelectedRoom(null);
                      setRoomAvailability([]);
                      setSegments([]);
                      setSelectedRange([]);
                      form.resetFields();

                      fetchAvailableRooms(checkIn, checkOut, {
                        preserveSelection: false,
                      });
                    }}
                  >
                    Clear
                  </Button>
                </Tooltip>
              </Space>
            </Col>
          </Row>
        </Card>
      </Affix>

      {/* Main planner grid */}
      <Row gutter={12}>
        {/* Calendar */}
        <Col xs={24} lg={16}>
          <Card
            size="small"
            title={
              <Space>
                <CalendarOutlined />
                <span>
                  {selectedRoom
                    ? `Room ${selectedRoom.roomNumber} • ${getTypeName(
                        selectedRoom
                      ) || ""}`
                    : "Select a room to view availability"}
                </span>
              </Space>
            }
            className="planner-calendar"
          >
            {!selectedRoom ? (
              <Empty description="Pick a room from the list to the right" />
            ) : (
              <Spin
                spinning={isBusy}
                tip={
                  loading.rooms
                    ? "Loading rooms..."
                    : loading.availability
                    ? "Loading availability..."
                    : undefined
                }
                delay={300}
              >

                
              <div className="planner-cal-wrap">
              <div className="planner-legend planner-legend-top">
                <span className="planner-legend-item">
                  <span className="planner-legend-swatch swatch-available" />
                  Available
                </span>
                <span className="planner-legend-item">
                  <span className="planner-legend-swatch swatch-booked" />
                  Unavailable / Booked
                </span>
                <span className="planner-legend-item">
                  <span className="planner-legend-swatch swatch-selected" />
                  Selected
                </span>
              </div>
              <Calendar
                key={calendarKey}
                fullscreen={false}
                defaultValue={dates?.[0] || dayjs()}   // allow user to navigate months normally
                validRange={dates?.[0] && dates?.[1] ? [dates[0], dates[1]] : undefined}
                onSelect={(d) => {
                  if (!isBusy) onCalendarSelect(d);
                }}

                fullCellRender={(date, info) => {
  if (info?.type !== "date") return info?.originNode;

  const d = dayjs(date);

  const winStart = dates?.[0] ? dayjs(dates[0]).startOf("day") : null;
  const winEnd   = dates?.[1] ? dayjs(dates[1]).startOf("day") : null;

  const hasTemp = selectedRange.length === 2;
  const hasAdded = !!isDateInAddedPieces(d);

  const winIsStart = !!winStart && d.isSame(winStart, "day");
  const winIsEnd   = !!winEnd && d.isSame(winEnd, "day");

  // Only show window badges if nothing else is driving badges
  const showWinStart = !hasTemp && !hasAdded && winIsStart;
  const showWinEnd   = !hasTemp && !hasAdded && winIsEnd;


  const { status } = dateIsInAvailableSegment(d);

  // temporary selection (before adding)
  const tempStart = selectedRange[0] ? dayjs(selectedRange[0]) : null;
  const tempEnd = selectedRange[1] ? dayjs(selectedRange[1]) : null;

  const tempSelected =
    selectedRange.length === 2 &&
    d.isBetween(dayjs(selectedRange[0]), dayjs(selectedRange[1]), null, "[)");

  const tempIsStart = !!tempStart && d.isSame(tempStart, "day");
  const tempIsEnd = !!tempEnd && d.isSame(tempEnd, "day");

  // already-added segments (after adding)
  const addedHit = isDateInAddedPieces(d);
  const addedSelected = !!addedHit;
  const addedIsStart = addedHit ? d.isSame(dayjs(addedHit.startISO), "day") : false;
  // show checkout badge on the checkout day (endISO), not last night
  const addedIsEnd = addedHit ? d.isSame(dayjs(addedHit.endISO), "day") : false;

  // final flags
  const isSelected = tempSelected || addedSelected;
  const isStart = tempIsStart || addedIsStart;
  const isEnd = tempIsEnd || addedIsEnd;

  return (
    <Tooltip
      title={
        isStart
          ? "Check-in"
          : isEnd
          ? "Check-out"
          : status === "available"
          ? "Available"
          : status === "booked"
          ? "Booked"
          : "Unavailable"
      }
    >
      <div
        className={[
          "planner-date-cell",
          status,
          isSelected ? "selected" : "",
          isStart ? "selected-start" : "",
          isEnd ? "selected-end" : "",
        ].join(" ")}
      >
        <span className="corner-date">{d.date()}</span>

        {status === "available" && (
          <span className="center-date">{d.date()}</span>
        )}

        {(isStart || showWinStart) && <span className="badge-start">Check-in</span>}
        {(isEnd   || showWinEnd)   && <span className="badge-end">Check-out</span>}

      </div>
    </Tooltip>
  );
                }}

              />
              </div>
              </Spin>
            )}

            {selectedRoom && selectedRange.length === 2 && (
              <Alert
                style={{ marginTop: 12 }}
                type="success"
                showIcon
                message={
                  <Row gutter={16} align="middle">
                    <Col flex="auto">
                      <Text strong>
                        Selected:&nbsp;
                        {dayjs(selectedRange[0]).format("ddd, MMM D")} →{" "}
                        {dayjs(selectedRange[1]).format("ddd, MMM D")}
                      </Text>
                      {selectedRoom?.conflictingDates?.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <Tag color="orange">
                            Conflicts: {selectedRoom.conflictingDates.length} day(s)
                          </Tag>
                        </div>
                      )}
                    </Col>
                    <Col>
                      <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={addSegment}
                      >
                        Add Segment
                      </Button>
                    </Col>
                    <Col>
                      <Space align="center">
                        <Switch
                          size="small"
                          checked={allowSupplement}
                          onChange={setAllowSupplement}
                        />
                        <Tooltip title="When on, you can auto-add same-type room segments to cover conflicts.">
                          <Text type="secondary">
                            Allow supplement (same type)
                          </Text>
                        </Tooltip>
                      </Space>
                    </Col>
                    <Col>
                      <Tooltip
                        title={
                          !selectedRoom?.conflictingDates?.length
                            ? "No conflicts to cover"
                            : allowSupplement
                            ? "Add segments from same-type rooms for conflict nights"
                            : "Turn on the supplement toggle first"
                        }
                      >
                        <Button
                          size="small"
                          disabled={
                            !allowSupplement ||
                            !(selectedRoom?.conflictingDates?.length > 0) ||
                            loading.rooms
                          }
                          onClick={autoFillConflicts}
                        >
                          Auto-fill conflicts
                        </Button>
                      </Tooltip>
                    </Col>
                  </Row>
                }
              />
            )}
          </Card>

          {currencyMismatch && (
            <Alert
              type="warning"
              showIcon
              message="Mixed currencies detected"
              description="All segments must use the same currency before confirming."
              style={{ marginTop: 12 }}
            />
          )}
        </Col>

        {/* Room list */}
        <Col xs={24} lg={8}>
          <Card
            size="small"
            title={
              <span>
                Rooms
                <span className="hms-count">{availableRooms.length}</span>
              </span>
            }
            bodyStyle={{
              padding: 0,
              maxHeight: "calc(100vh - 220px)",
              overflow: "auto",
            }}
          >
            {loading.rooms && availableRooms.length === 0 ? (
              <div style={{ padding: 16 }}>
                <Skeleton active paragraph={{ rows: 6 }} />
              </div>
            ) : availableRooms.length === 0 ? (
              <div style={{ padding: 16 }}>
                <Empty description="Select dates to load available rooms" />
              </div>
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={availableRooms}
                renderItem={(room) => (
                  <List.Item
                    style={{
                      padding: "12px 16px",
                      borderLeft:
                        selectedRoom?.roomId === room.roomId
                          ? "3px solid var(--hms-accent)"
                          : "3px solid transparent",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSelectRoom(room)}
                    actions={[
                      nightlyTag(room),
                      <Tooltip title="View" key="v">
                        <Button
                          size="small"
                          type="text"
                          icon={<EyeOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectRoom(room);
                          }}
                        />
                      </Tooltip>,


 <Tooltip title="Add for full date range" key="add">
   <Button
     size="small"
     type="text"
     icon={<PlusOutlined />}
     onClick={(e) => {
       e.stopPropagation();
       addRoomCoverRemaining(room);            // full window; no conflicts needed
     }}
   />
  </Tooltip>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>Room {room.roomNumber}</Text>
                          <StatusTag status={room.status}>{room.status || "Unknown"}</StatusTag>
                          {room.status === "PartiallyAvailable" &&
                            room.conflictingDates && (
                              <Tooltip
                                title={
                                  <div>
                                    <div>Unavailable dates:</div>
                                    {room.conflictingDates.map((date) => (
                                      <div key={date}>
                                        {dayjs(date).format("MMM D")}
                                      </div>
                                    ))}
                                  </div>
                                }
                              >
                                <Tag color="orange">
                                  Conflicts: {room.conflictingDates.length} days
                                </Tag>
                              </Tooltip>
                            )}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={2}>
                          <Space split={<Divider type="vertical" />}>
                            <Text type="secondary">
                              {getTypeName(room) || "Type"}
                            </Text>
                            <Text type="secondary">
                              {(room.currency || "BWP") +
                                " " +
                                fmt.format(room.price || 0)}
                            </Text>
                          </Space>

                          {room.conflictingDates &&
                            room.conflictingDates.length > 0 && (
                              <div>
                                <Text
                                  type="danger"
                                  style={{ fontSize: "12px" }}
                                >
                                  Unavailable:{" "}
                                  {room.conflictingDates
                                    .slice(0, 3)
                                    .map((date) =>
                                      dayjs(date).format("MMM D")
                                    )
                                    .join(", ")}
                                  {room.conflictingDates.length > 3 &&
                                    ` +${
                                      room.conflictingDates.length - 3
                                    } more`}
                                </Text>
                              </div>
                            )}

                          {(room.availabilitySegments || [])
                            .filter((s) => s.status === "Booked")
                            .slice(0, 2)
                            .map((s, i) => (
                              <Tag key={i} color="volcano" style={{ marginTop: 4 }}>
                                Booked: {dayjs(s.start).format("MMM D")} →{" "}
                                {dayjs(s.end).format("MMM D")}
                              </Tag>
                            ))}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Guest Drawer */}
      <GuestDrawer
        open={guestDrawerOpen}
        onClose={() => setGuestDrawerOpen(false)}
        form={form}
      />

      {/* Breakdown modal */}
      <Modal
        open={breakdownOpen}
        title="Booking breakdown"
        onCancel={() => setBreakdownOpen(false)}
        footer={null}
        width={940}
        centered
        className="hms-modal"
      >
        <Table
          className="hms-table"
          scroll={{ x: "max-content" }}
          columns={[
            { title: "#", dataIndex: "index", width: 48 },
            { title: "Room", dataIndex: "room" },
            { title: "Type", dataIndex: "type" },
            { title: "Dates", dataIndex: "dates" },
            { title: "Nights", dataIndex: "nights", align: "right", width: 90 },
            {
              title: "Base",
              dataIndex: "base",
              align: "right",
              width: 120,
              render: (v, row) => (
                <Text>
                  {row.currency || currencyForTop} {fmt.format(v || 0)}
                </Text>
              ),
            },
            {
              title: "VAT",
              dataIndex: "vat",
              align: "right",
              width: 120,
              render: (v, row) => (
                <Text>
                  {row.currency || currencyForTop} {fmt.format(v || 0)}
                </Text>
              ),
            },
            {
              title: "Levies",
              dataIndex: "lev",
              align: "right",
              width: 120,
              render: (v, row) => (
                <Text>
                  {row.currency || currencyForTop} {fmt.format(v || 0)}
                </Text>
              ),
            },
            {
              title: "Discount",
              dataIndex: "discount",
              align: "right",
              width: 120,
              render: (v, row) => (
                <Text type={v > 0 ? "danger" : "secondary"}>
                  {v > 0 ? "-" : ""}
                  {row.currency || currencyForTop} {fmt.format(v || 0)}
                </Text>
              ),
            },
            {
              title: "Amount",
              dataIndex: "amount",
              align: "right",
              width: 150,
              render: (v, row) => (
                <Text strong>
                  {row.currency || currencyForTop} {fmt.format(v || 0)}
                </Text>
              ),
            },
          ]}
          dataSource={priceRows}
          pagination={false}
          rowKey="key"
          summary={() => (
            <Table.Summary>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <Text strong>Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong>{totals.nights || 0}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <Text strong>
                    {currencyForTop} {fmt.format(totals.base || 0)}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <Text strong>
                    {currencyForTop} {fmt.format(totals.vat || 0)}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <Text strong>
                    {currencyForTop} {fmt.format(totals.lev || 0)}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <Text strong>
                    -{currencyForTop} {fmt.format(totals.discount || 0)}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  <Text strong style={{ fontSize: 16 }}>
                    {currencyForTop} {fmt.format(totals.amount || 0)}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Modal>
    </div>
  );
};

export default BookingPage;
